import { Socket } from 'socket.io-client'
import type { MessageType, UserOnline } from '~/types/Chat'
import type { ResponseType } from '~/types/Task'
import type { UserType } from '~/types/User'

// Интерфейс для состояния сокета
interface SocketState {
    isConnected: boolean
    messages: MessageType[]
    responses: ResponseType[]
    usersOnline: UserOnline[]
    unreadMessages: number
}

// Глобальное состояние для всех экземпляров useSocket
let globalState: SocketState | null = null
let listenersInitialized = false

// Глобальные handlers для всех экземпляров
const messageHandlers = new Set<(message: MessageType) => void>()
const responseHandlers = new Set<(response: ResponseType) => void>()
const usersOnlineHandlers = new Set<(users: UserOnline[]) => void>()
const unreadMessagesHandlers = new Set<(unreadMessages: number) => void>()

export const useSocket = () => {
    if (process.server) {
        return {
            isConnected: ref(false),
            messages: ref([]),
            responses: ref([]),
            usersOnline: ref([]),
            unreadMessages: ref(0),
            connect: () => {},
            disconnect: () => {},
            onMessage: () => () => {},
            onResponseState: () => () => {},
            onUsersOnline: () => () => {},
            onUnreadMessages: () => () => {},
            subscribeToUsers: () => {},
            cleanup: () => {}
        }
    }

    const nuxtApp = useNuxtApp()
    const socket: Socket | undefined = nuxtApp.$ioSocket

    if (!socket) {
        console.error('Socket is not available - plugin may not be loaded yet')
        return {
            isConnected: ref(false),
            messages: ref([]),
            responses: ref([]),
            usersOnline: ref([]),
            unreadMessages: ref(0),
            connect: () => {},
            disconnect: () => {},
            onMessage: () => () => {},
            onResponseState: () => () => {},
            onUsersOnline: () => () => {},
            onUnreadMessages: () => () => {},
            subscribeToUsers: () => {},
            cleanup: () => {}
        }
    }

    if (!globalState) {
        globalState = reactive<SocketState>({
            isConnected: socket?.connected || false,
            messages: [] as MessageType[],
            responses: [] as ResponseType[],
            usersOnline: [] as UserOnline[],
            unreadMessages: 0
        })
    }

    const state = globalState

    const connect = (token: string) => {
        if (!token || !socket) {
            console.error('Token is required or socket is not available')
            return
        }

        socket.io.opts.query = {
            token: token
        }

        socket.connect()
        state.isConnected = socket.connected || false
    }

    const onMessage = (handler: (message: MessageType) => void) => {
        messageHandlers.add(handler)
        return () => messageHandlers.delete(handler)
    }

    const onResponseState = (handler: (response: ResponseType) => void) => {
        responseHandlers.add(handler)
        return () => responseHandlers.delete(handler)
    }

    const onUsersOnline = (handler: (users: UserOnline[]) => void) => {
        usersOnlineHandlers.add(handler)
        return () => usersOnlineHandlers.delete(handler)
    }

    const onUnreadMessages = (handler: (unreadMessages: number) => void) => {
        unreadMessagesHandlers.add(handler)
        return () => unreadMessagesHandlers.delete(handler)
    }

    const subscribeToUsers = (userIds: string[]) => {
        if (!socket || !socket.connected) {
            console.warn('Socket is not connected. Cannot subscribe to users.')
            return
        }
        socket.emit('user:state', userIds)
    }

    const initializeListeners = () => {
        if (!socket) return
        
        state.isConnected = socket.connected || false

        socket.on('connect', () => {
            state.isConnected = true
            console.log('Connected socket')
        })

        socket.on('disconnect', () => {
            state.isConnected = false
            console.log('Disconnected socket')
        })

        socket.on('message', (message: MessageType) => {
            console.log('useSocket message', messageHandlers)
            messageHandlers.forEach(handler => handler(message))
        })

        socket.on('response', (response: ResponseType) => {
            responseHandlers.forEach(handler => handler(response))
        })

        socket.on('user:state', (users: UserOnline[]) => {
            users.forEach((user: UserOnline) => {
                const existingIndex = state.usersOnline.findIndex((u: UserOnline) => u.userId === user.userId)
                if (existingIndex !== -1) {
                    state.usersOnline[existingIndex] = user
                } else {
                    state.usersOnline.push(user)
                }
            })
            usersOnlineHandlers.forEach(handler => handler(users))
        })

        socket.on('unread', (unreadMessages: number) => {
            unreadMessagesHandlers.forEach(handler => handler(unreadMessages))
        })

        socket.on('user', (userData: Partial<UserType>) => {
            console.log('useSocket user event', userData)
            if (process.client) {
                const userStore = useUserStore()
                userStore.updateUserPartial(userData)
            }
        })

        socket.on('error', (error) => {
            console.error('Socket error:', error)
        })
    }

    if (!listenersInitialized && process.client) {
        initializeListeners()
        listenersInitialized = true
    }

    const cleanup = () => {
        console.log('cleanup socket')
        
        if (!socket) return
        
        socket.off('connect')
        socket.off('disconnect')
        socket.off('message')
        socket.off('response')
        socket.off('user:state')
        socket.off('unread')
        socket.off('user')
        socket.off('error')

        messageHandlers.clear()
        responseHandlers.clear()
        usersOnlineHandlers.clear()
        unreadMessagesHandlers.clear()

        socket.disconnect()
        state.isConnected = socket.connected || false
    }

    return {
        ...toRefs(state),
        connect,
        disconnect: () => socket?.disconnect(),
        onMessage,
        onResponseState,
        onUsersOnline,
        onUnreadMessages,
        subscribeToUsers,
        cleanup
    }
}